import React, { HTMLAttributes } from 'react';
import SearchInput from '@/components/SearchInput';
import UserInitials from '@/trio-ui/UserInitials';
import * as Styles from './PersonSearch.styles';
import { shouldSearchPersonResults } from './PersonSearch.utils';

export interface Person {
  id: string;
  firstName: string;
  lastName: string;
  developerType?: string;
  pictureUrl?: string;
}

export interface PersonSearchComponentProps {
  name?: string;
  label?: string;
  searchTerm: string;
  showResults: boolean;
  persons: Person[];
  isLoading: boolean;
  placeholder?: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPersonSelect: (person: Person) => void;
  errorMessage?: string;
}

const PersonSearchComponent = ({
  name,
  label,
  searchTerm,
  showResults,
  persons,
  isLoading,
  placeholder = 'Search for a person...',
  onSearchChange,
  onPersonSelect,
  errorMessage,
  ...props
}: PersonSearchComponentProps): JSX.Element => {
  const renderSearchResults = (): JSX.Element | null => {
    if (!showResults) return null;

    if (isLoading) {
      return (
        <Styles.SearchResults>
          <Styles.LoadingContainer>
            <div>Loading...</div>
          </Styles.LoadingContainer>
        </Styles.SearchResults>
      );
    }

    const hasPersonResults = persons.length > 0;
    if (hasPersonResults) {
      return (
        <Styles.SearchResults>
          {persons.map((person) => (
            <Styles.SearchResultItem key={person.id} onClick={() => onPersonSelect(person)}>
              <div className="flex align-center">
                <UserInitials
                  size={24}
                  pictureUrl={person?.pictureUrl ?? ''}
                  firstName={person.firstName}
                  lastName={person.lastName}
                />

                <div>
                  <Styles.PersonName>
                    {person.firstName} {person.lastName}
                  </Styles.PersonName>
                  {person.developerType && (
                    <Styles.PersonType>{person.developerType}</Styles.PersonType>
                  )}
                </div>
              </div>
            </Styles.SearchResultItem>
          ))}
        </Styles.SearchResults>
      );
    }

    if (shouldSearchPersonResults(searchTerm)) {
      return (
        <Styles.SearchResults>
          <Styles.NoResultsContainer>
            <div>No persons found</div>
          </Styles.NoResultsContainer>
        </Styles.SearchResults>
      );
    }

    return null;
  };

  return (
    <Styles.PersonSearchContainer>
      <SearchInput
        label={label}
        name={name}
        id="person-search"
        value={searchTerm}
        onChangeHandler={onSearchChange}
        placeholder={placeholder}
        testId="person-search-input"
        errorMessage={errorMessage}
        {...props}
      />
      {renderSearchResults()}
    </Styles.PersonSearchContainer>
  );
};

export default PersonSearchComponent;
